import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LAXHJALPEN_STUDENT_LIST_QUERY_KEY } from '../../../../../FrontEndReact/src/Core/Hooks/useLaxhjalpenStudentList';
import { emailValidator, requiredValidator, useForm } from '../../../../../FrontEndReact/src/Core/Hooks/useForm';
import { useBizMutation, useBizQuery } from '../../../../../FrontEndReact/src/Core/Hooks/useBizQuery';
import { User } from '../../../../../FrontEndReact/src/Core/Types/user';
import { getStudentRegistrationValidation } from '../../../../../FrontEndReact/src/Core/Services/laxhjalpenStudentRegistration';
import { getPortalId } from '../../../../../FrontEndReact/src/Core/Services/common';
import { validateStudentSsn } from '../../../../../FrontEndReact/src/Core/Utils/studentSsnValidation';
import {
	getAllowedStudentRegistrationUnitTypeIds,
	getHomeworkHelpers,
	getStudents,
	registerStudent,
	saveHomeworkHelperStudentLink,
} from '../Services/studentRegistration';

const QUERYKEY_ALLOWED_STUDENT_REGISTRATION_UNIT_TYPE_IDS = 'LAXHJALPEN_ALLOWED_STUDENT_REGISTRATION_UNIT_TYPE_IDS';
const QUERYKEY_HOMEWORK_HELPERS = 'LAXHJALPEN_HOMEWORK_HELPERS';
const QUERYKEY_STUDENTS = 'LAXHJALPEN_REGISTRATION_STUDENTS';

export type GuardianFormState = {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	mobile: string;
};

export type StudentFormState = {
	ssn: string;
	email: string;
	firstName: string;
	lastName: string;
	unitId: number;
	unitTypeId: number;
	homeworkHelperUserId: number;
};

const createGuardian = (index: number): GuardianFormState => ({
	id: `guardian_${Date.now()}_${index}`,
	email: '',
	firstName: '',
	lastName: '',
	mobile: '',
});

export const useHomeworkHelpers = (unitId: number) => {
	const { data: helpers, statusState } = useBizQuery(
		[QUERYKEY_HOMEWORK_HELPERS, unitId],
		async () => {
			if (!unitId || unitId <= 0) {
				return [];
			}

			const data = await getHomeworkHelpers(unitId);
			return data.sort((left, right) => left.Name.localeCompare(right.Name));
		},
		unitId > 0
	);

	return {
		helpers: helpers ?? [],
		isLoading: statusState === 'LOADING',
	};
};

export const useRegisterStudent = () => {
	const { mutate, statusState, data } = useBizMutation(registerStudent);

	return {
		createStatus: statusState,
		createResult: data,
		registerStudent: mutate,
	};
};

export const useStudents = (unitId: number, enabled: boolean) => {
	const { data: students, statusState } = useBizQuery(
		[QUERYKEY_STUDENTS, unitId],
		async () => {
			if (!unitId || unitId <= 0) {
				return [];
			}

			const data = await getStudents(unitId);
			return data.sort((left, right) => getUserDisplayName(left).localeCompare(getUserDisplayName(right)));
		},
		enabled && unitId > 0
	);

	return {
		students: students ?? [],
		statusState,
		isLoading: statusState === 'LOADING',
	};
};

export const useSaveHomeworkHelperStudentLink = (unitId: number) => {
	const queryClient = useQueryClient();
	const { mutate, statusState, data } = useBizMutation(saveHomeworkHelperStudentLink, {
		onSuccessFn: () => {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_STUDENTS, unitId] });
		},
	});

	return {
		saveStatus: statusState,
		saveResult: data,
		saveHomeworkHelperStudentLink: mutate,
	};
};

export const useAllowedStudentRegistrationUnitTypeIds = () => {
	const { data: allowedUnitTypeIds, statusState } = useBizQuery(
		[QUERYKEY_ALLOWED_STUDENT_REGISTRATION_UNIT_TYPE_IDS],
		async () => await getAllowedStudentRegistrationUnitTypeIds()
	);

	return {
		allowedUnitTypeIds: allowedUnitTypeIds ?? [],
		isLoading: statusState === 'LOADING',
	};
};

export function useLaxhjalpenStudentRegistration({
	helperRequired = false,
	customSetDirtyFn,
}: {
	helperRequired?: boolean;
	customSetDirtyFn?: (isDirty: boolean) => void;
}) {
	const { formState, clearFormState } = useForm({
		id: 'laxhjalpen-student-registration',
		customSetDirtyFn,
	});
	const { createStatus, createResult, registerStudent } = useRegisterStudent();
	const queryClient = useQueryClient();
	const [student, setStudent] = useState<StudentFormState>({
		email: '',
		firstName: '',
		lastName: '',
		ssn: '',
		unitId: -1,
		unitTypeId: -1,
		homeworkHelperUserId: -1,
	});
	const [guardians, setGuardians] = useState<GuardianFormState[]>([createGuardian(0)]);
	const { helpers } = useHomeworkHelpers(student.unitId);
	const { allowedUnitTypeIds } = useAllowedStudentRegistrationUnitTypeIds();
	const { data: validation, statusState: validationStatus } = useBizQuery(
		['LAXHJALPEN_STUDENT_REGISTRATION_VALIDATION', getPortalId()],
		getStudentRegistrationValidation
	);
	const ssnValidationResult = validateStudentSsn(student.ssn, validation?.ssnRegex);
	const ssnValidator = (value: string) =>
		!value.trim() || (validationStatus === 'SUCCESS' && validateStudentSsn(value, validation?.ssnRegex) === 'valid');
	const isSsnValid = ssnValidator(student.ssn);
	const ssnValidationMessageKey = !student.ssn.trim()
		? null
		: validationStatus === 'ERROR'
			? 'SSN_VALIDATION_UNAVAILABLE'
			: ssnValidationResult === 'invalid-pattern'
				? 'SSN_INVALID_PATTERN'
				: ssnValidationResult === 'invalid-format'
					? 'SSN_INVALID_FORMAT'
					: null;
	const isStudentUnitTypeAllowed = student.unitId <= 0 || allowedUnitTypeIds.includes(student.unitTypeId);
	const shouldShowUnitTypeValidation = student.unitId > 0 && !isStudentUnitTypeAllowed;
	const isReadyToSubmit = formState.isFormValid && isSsnValid && student.unitId > 0 && isStudentUnitTypeAllowed;

	useEffect(() => {
		if (student.homeworkHelperUserId > 0 && helpers.some((helper) => helper.Id === student.homeworkHelperUserId) === false) {
			setStudent((current) => ({
				...current,
				homeworkHelperUserId: -1,
			}));
		}
	}, [helpers, student.homeworkHelperUserId]);

	useEffect(() => {
		if (createStatus === 'SUCCESS' && createResult?.Success === true) {
			queryClient.invalidateQueries({ queryKey: [LAXHJALPEN_STUDENT_LIST_QUERY_KEY] });
			clearFormState();
			setStudent({
				email: '',
				firstName: '',
				lastName: '',
				ssn: '',
				unitId: -1,
				unitTypeId: -1,
				homeworkHelperUserId: -1,
			});
			setGuardians([createGuardian(0)]);
		}
	}, [createResult, createStatus]);

	function updateStudentField(field: keyof StudentFormState, value: string | number) {
		setStudent((current) => ({
			...current,
			[field]: value,
		}));
		formState.setDirty();
	}

	function updateGuardianField(guardianId: string, field: keyof Omit<GuardianFormState, 'id'>, value: string) {
		setGuardians((current) =>
			current.map((guardian) =>
				guardian.id === guardianId
					? {
							...guardian,
							[field]: value,
						}
					: guardian
			)
		);
		formState.setDirty();
	}

	function addGuardian() {
		setGuardians((current) => [...current, createGuardian(current.length)]);
		formState.setDirty();
	}

	function removeGuardian(guardianId: string) {
		setGuardians((current) => {
			const removedGuardian = current.find((guardian) => guardian.id === guardianId);
			if (removedGuardian) {
				formState.removeField(`${removedGuardian.id}_email`);
				formState.removeField(`${removedGuardian.id}_firstName`);
				formState.removeField(`${removedGuardian.id}_lastName`);
				formState.removeField(`${removedGuardian.id}_mobile`);
			}
			return current.filter((guardian) => guardian.id !== guardianId);
		});
		formState.setDirty();
	}

	function onSubmit() {
		if (createStatus === 'LOADING') {
			return;
		}

		if (
			!formState.isFormValid ||
			!isSsnValid ||
			student.unitId <= 0 ||
			!isStudentUnitTypeAllowed ||
			(helperRequired && student.homeworkHelperUserId <= 0)
		) {
			formState.setShowValidation(true);
			return;
		}

		registerStudent({
			email: student.email,
			firstName: student.firstName,
			lastName: student.lastName,
			ssn: student.ssn,
			unitId: student.unitId,
			homeworkHelperUserId: student.homeworkHelperUserId > 0 ? student.homeworkHelperUserId : null,
			guardians: guardians.map((guardian) => ({
				email: guardian.email,
				firstName: guardian.firstName,
				lastName: guardian.lastName,
				mobile: guardian.mobile,
			})),
		});
	}

	const helperOptions = helpers.map((helper) => ({
		Id: helper.Id,
		Name: `${helper.Name}${helper.Email ? ` (${helper.Email})` : ''}`,
	}));

	return {
		addGuardian,
		clearFormState,
		createResult,
		createStatus,
		emailValidator,
		formState,
		guardians,
		helperOptions,
		helperRequired,
		isStudentUnitTypeAllowed,
		isReadyToSubmit,
		onSubmit,
		removeGuardian,
		requiredValidator,
		shouldShowUnitTypeValidation,
		ssnValidator,
		ssnValidationMessageKey,
		student,
		updateGuardianField,
		updateStudentField,
	};
}

export function getUserDisplayName(user: User) {
	return (
		user.Name || user.FullName || `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim() || user.Email || user.UserName || ''
	);
}
